import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  OnInit
} from '@angular/core';
import {MatIconModule} from '@angular/material/icon';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {CommonModule} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';
import {combineLatest, Observable} from 'rxjs';
import {map, startWith, tap} from 'rxjs/operators';
import {Q9HtmlDecoderPipe} from '@q9elements/ui-kit/common';
import {IconButtonComponent} from '@q9elements/ui-kit/components';
import {NavigationService} from 'src/app/core/navigation/navigation.service';
import {FlowlineAssociationService} from 'src/app/core/services/flowline-association.service';
import {ModeDataService} from 'src/app/editor-state/mode-data.service';
import {Link} from 'src/app/shared/models/connector-link.interface';
import {ScreenshotPopoverDirective} from 'src/app/editor-interface/shared/directives/screenshot-popover.directive';

interface ScreenConfig {
  id: string;
  state: string;
  size: string;
}

interface DataForFlowlines {
  links: Link[];
  loadingLinks: boolean;
  isEditMode: boolean;
}

@Component({
  selector: 'app-connector-list',
  templateUrl: './connector-list.component.html',
  styleUrls: ['./connector-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    MatProgressSpinnerModule,
    ScreenshotPopoverDirective,
    TranslateModule,
    MatIconModule,
    Q9HtmlDecoderPipe,
    IconButtonComponent
  ]
})
export class ConnectorListComponent implements OnChanges, OnInit {
  @Input() public flMxKey: string | number = '';

  public dataForFlowlines$: Observable<DataForFlowlines>;
  public version: string;

  constructor(
    private navigationService: NavigationService,
    private flowlineAssociationService: FlowlineAssociationService,
    public modeDataService: ModeDataService
  ) {}

  ngOnInit(): void {
    const links$ = this.flowlineAssociationService.links$.pipe(startWith([] as Link[]));
    const refresh$ = this.flowlineAssociationService.refresh$.pipe(
      tap(refresh => refresh && this.flowlineAssociationService.initLinks(String(this.flMxKey)))
    );

    this.dataForFlowlines$ = combineLatest([
      links$,
      this.flowlineAssociationService.loadingLinks$,
      this.modeDataService.isEditMode$,
      refresh$
    ]).pipe(map(([links, loadingLinks, isEditMode]) => ({links, loadingLinks, isEditMode})));

    this.version = this.navigationService.getCurrent().state;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.flMxKey && changes.flMxKey) {
      this.flowlineAssociationService.initLinks(String(this.flMxKey));
    }
  }

  public navigate(link: Link, event: Event): void {
    event.preventDefault();
    this.flowlineAssociationService.navigate(link);
  }

  public trackLinksById(_: number, item: Link): string {
    return item.connectorId;
  }

  public disconnect(linkId: string): void {
    this.flowlineAssociationService.disconnect(linkId);
  }

  public getConfigForScreen(id: string): ScreenConfig {
    return {
      id,
      state: this.navigationService.getCurrent().state,
      size: '_600x400'
    };
  }
}
